// src/pages/Drivers.js
import React, { useEffect, useState, useRef } from "react";
import { db, collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where, serverTimestamp } from "../firebase";
import { v4 as uuidv4 } from "uuid";
import LoadingSpinner from "../components/LoadingSpinner";
import ConfirmModal from "../components/ConfirmModal";

const FeedbackModal = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: "400px", padding: "20px" }}>
        <h3>{type === "success" ? "Success" : "Error"}</h3>
        <p style={{ color: type === "success" ? "#15803d" : "#b91c1c" }}>{message}</p>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ driver, onEdit, onReassign, onDelete, onClose, hasActiveTrips }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!driver || !driver.id || !driver.full_name) {
    console.error("ActionModal: Invalid driver object:", driver);
    return null;
  }

  return (
    <div className="modal-overlay">
      <div className="modal" ref={modalRef}>
        <h3>Actions for {driver.full_name}</h3>
        <div className="modal-actions">
          <button className="btn-secondary" onClick={() => onEdit(driver)}>
            Edit Driver
          </button>
          <button
            className="btn-secondary"
            onClick={() => onReassign(driver)}
            disabled={hasActiveTrips(driver.id)}
            title={hasActiveTrips(driver.id) ? "Cannot reassign: Driver has active trips" : ""}
          >
            Re-assign Truck
          </button>
          <button
            className="btn-delete"
            onClick={() => onDelete(driver)}
            disabled={hasActiveTrips(driver.id)}
            title={hasActiveTrips(driver.id) ? "Cannot delete: Driver has active trips" : ""}
          >
            Delete Driver
          </button>
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default function Drivers() {
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    id_number: "",
    prdp: "",
    prdp_expiry: "",
    assigned_truck: "",
  });
  const [editingDriverId, setEditingDriverId] = useState(null);
  const [reassignDriverId, setReassignDriverId] = useState(null);
  const [actionModalDriver, setActionModalDriver] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // New state for confirm modal
  const [openDropdown, setOpenDropdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    async function loadAll() {
      setLoading(true);
      setFeedback(null);
      try {
        await Promise.all([loadDrivers(), loadTrucks(), loadTrips()]);
        console.log("Drivers, trucks, and trips data loaded");
      } catch (error) {
        console.error("Error loading data:", error.code, error.message, error.stack);
        setFeedback({
          message: `Failed to load data: ${error.message}`,
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
    loadAll();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        closeModal();
      }
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function loadDrivers() {
    try {
      const snaps = await getDocs(collection(db, "drivers"));
      const driverData = snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
      console.log("Loaded drivers:", driverData);
      setDrivers(driverData);
    } catch (error) {
      console.error("Error fetching drivers:", error.code, error.message);
      setDrivers([]);
      throw error;
    }
  }

  async function loadTrucks() {
    try {
      const snaps = await getDocs(collection(db, "trucks"));
      const truckData = snaps.docs.map((t) => ({ id: t.id, reg_no: t.data().reg_no || "Unknown" })) || [];
      console.log("Loaded trucks:", truckData);
      setTrucks(truckData);
    } catch (error) {
      console.error("Error fetching trucks:", error.code, error.message);
      setTrucks([]);
      throw error;
    }
  }

  async function loadTrips() {
    try {
      const snaps = await getDocs(collection(db, "trips"));
      const tripData = snaps.docs.map((d) => ({ id: d.id, ...d.data() })) || [];
      console.log("Loaded trips:", tripData);
      setTrips(tripData);
    } catch (error) {
      console.error("Error fetching trips:", error.code, error.message);
      setTrips([]);
      throw error;
    }
  }

  async function validateField(field, value, excludeId = null) {
    if (!value) return true;
    try {
      const q = query(collection(db, "drivers"), where(field, "==", value));
      const snaps = await getDocs(q);
      return snaps.docs.every((d) => d.id === excludeId);
    } catch (error) {
      console.error(`Error validating ${field}:`, error.code, error.message);
      throw error;
    }
  }

  async function create(e) {
    e.preventDefault();
    setFeedback(null);
    try {
      const { full_name, phone, id_number, prdp } = form;
      if (!full_name) {
        setFeedback({ message: "Data error: Full name is required.", type: "error" });
        return;
      }
      if (phone && !/^\d{10}$/.test(phone)) {
        setFeedback({ message: "Data error: Phone must be a 10-digit number.", type: "error" });
        return;
      }
      if (!(await validateField("id_number", id_number))) {
        setFeedback({ message: "Data error: ID/Passport number must be unique.", type: "error" });
        return;
      }
      if (!(await validateField("prdp", prdp))) {
        setFeedback({ message: "Data error: PrDP number must be unique.", type: "error" });
        return;
      }
      const payload = {
        ...form,
        createdAt: serverTimestamp(),
        driver_code: "DRV-" + uuidv4().slice(0, 8),
      };
      console.log("Creating driver with payload:", payload);
      await addDoc(collection(db, "drivers"), payload);
      setForm({
        full_name: "",
        phone: "",
        id_number: "",
        prdp: "",
        prdp_expiry: "",
        assigned_truck: "",
      });
      await loadDrivers();
      setFeedback({ message: "Driver added successfully.", type: "success" });
    } catch (error) {
      console.error("Error creating driver:", error.code, error.message);
      setFeedback({ message: `Failed to create driver: ${error.message}`, type: "error" });
    }
  }

  async function updateDriver(e) {
    e.preventDefault();
    if (!editingDriverId) return;
    setFeedback(null);
    try {
      const { full_name, phone, id_number, prdp } = form;
      if (!full_name) {
        setFeedback({ message: "Data error: Full name is required.", type: "error" });
        return;
      }
      if (phone && !/^\d{10}$/.test(phone)) {
        setFeedback({ message: "Data error: Phone must be a 10-digit number.", type: "error" });
        return;
      }
      if (!(await validateField("id_number", id_number, editingDriverId))) {
        setFeedback({ message: "Data error: ID/Passport number must be unique.", type: "error" });
        return;
      }
      if (!(await validateField("prdp", prdp, editingDriverId))) {
        setFeedback({ message: "Data error: PrDP number must be unique.", type: "error" });
        return;
      }
      console.log("Updating driver:", editingDriverId, "with payload:", form);
      await updateDoc(doc(db, "drivers", editingDriverId), form);
      closeModal();
      await loadDrivers();
      setFeedback({ message: "Driver updated successfully.", type: "success" });
    } catch (error) {
      console.error("Error updating driver:", error.code, error.message);
      setFeedback({ message: `Failed to update driver: ${error.message}`, type: "error" });
    }
  }

  async function reassignTruck(e) {
    e.preventDefault();
    if (!reassignDriverId) return;
    setFeedback(null);
    try {
      console.log("Reassigning truck for driver:", reassignDriverId, "to:", form.assigned_truck);
      await updateDoc(doc(db, "drivers", reassignDriverId), {
        assigned_truck: form.assigned_truck,
      });
      closeModal();
      await loadDrivers();
      setFeedback({ message: "Truck reassigned successfully.", type: "success" });
    } catch (error) {
      console.error("Error reassigning truck:", error.code, error.message);
      setFeedback({ message: `Failed to reassign truck: ${error.message}`, type: "error" });
    }
  }

  // FIXED: Delete function - now accepts driver object and uses driver.id directly
  async function deleteDriver(driver) {
    try {
      console.log("Deleting driver with ID:", driver.id);
      await deleteDoc(doc(db, "drivers", driver.id));
      await loadDrivers();
      setFeedback({ message: "Driver deleted successfully.", type: "success" });
    } catch (error) {
      console.error("Error deleting driver:", error.code, error.message);
      setFeedback({ message: `Failed to delete driver: ${error.message}`, type: "error" });
    }
  }

  function hasActiveTrips(driverId) {
    const driver = drivers.find((d) => d.id === driverId);
    if (!driver) return false;
    return trips.some((t) => t.driver === driver.full_name && t.status === "Active");
  }

  function openEditModal(driver) {
    setForm({
      full_name: driver.full_name || "",
      phone: driver.phone || "",
      id_number: driver.id_number || "",
      prdp: driver.prdp || "",
      prdp_expiry: driver.prdp_expiry || "",
      assigned_truck: driver.assigned_truck || "",
    });
    setEditingDriverId(driver.id);
    setReassignDriverId(null);
    setActionModalDriver(null);
  }

  function openReassignModal(driver) {
    setForm({ ...form, assigned_truck: driver.assigned_truck || "" });
    setReassignDriverId(driver.id);
    setEditingDriverId(null);
    setActionModalDriver(null);
  }

  function openDeleteConfirm(driver) {
    setConfirmModal({
      title: "Delete Driver",
      message: `Are you sure you want to delete "${driver.full_name}"? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "danger",
      onConfirm: () => deleteDriver(driver),
    });
    setActionModalDriver(null);
  }

  function closeModal() {
    setEditingDriverId(null);
    setReassignDriverId(null);
    setActionModalDriver(null);
    setConfirmModal(null);
    setForm({
      full_name: "",
      phone: "",
      id_number: "",
      prdp: "",
      prdp_expiry: "",
      assigned_truck: "",
    });
    setFeedback(null);
  }

  const renderDropdown = (label, field, options) => {
    const isPlaceholder = !form[field];
    return (
      <div className="dropdown-wrapper" ref={dropdownRef}>
        <div
          className={`select-control dropdown-input ${openDropdown === field ? "active" : ""} ${
            isPlaceholder ? "placeholder" : ""
          }`}
          onClick={() => setOpenDropdown(openDropdown === field ? null : field)}
        >
          {isPlaceholder ? `Select ${label}` : form[field]}
        </div>
        {openDropdown === field && (
          <div className="dropdown">
            <div
              className={`dropdown-option ${!form[field] ? "selected" : ""}`}
              onClick={() => {
                setForm({ ...form, [field]: "" });
                setOpenDropdown(null);
              }}
            >
              None
            </div>
            {options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={opt}
                  className={`dropdown-option ${form[field] === opt ? "selected" : ""}`}
                  onClick={() => {
                    setForm({ ...form, [field]: opt });
                    setOpenDropdown(null);
                  }}
                >
                  {opt}
                </div>
              ))
            ) : (
              <div className="dropdown-option">No trucks available</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="drivers-page">
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <h2>Drivers</h2>
          <form onSubmit={create} className="form-inline">
            <input
              placeholder="Full name"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              required
            />
            <input
              placeholder="Phone (10 digits)"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <input
              placeholder="ID/Passport"
              value={form.id_number}
              onChange={(e) => setForm({ ...form, id_number: e.target.value })}
            />
            <input
              placeholder="PrDP No"
              value={form.prdp}
              onChange={(e) => setForm({ ...form, prdp: e.target.value })}
            />
            <input
              type="date"
              placeholder="PrDP Expiry"
              value={form.prdp_expiry}
              onChange={(e) => setForm({ ...form, prdp_expiry: e.target.value })}
            />
            {renderDropdown("Assigned Truck", "assigned_truck", trucks.map((t) => t.reg_no) || [])}
            <button className="btn-primary" type="submit">
              Add
            </button>
          </form>
          {feedback && !editingDriverId && !reassignDriverId && !actionModalDriver && (
            <FeedbackModal
              message={feedback.message}
              type={feedback.type}
              onClose={() => setFeedback(null)}
            />
          )}
          <div className="table-wrap">
            <table className="simple-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>PrDP</th>
                  <th>PrDP Expiry</th>
                  <th>Assigned Truck</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {drivers.length === 0 ? (
                  <tr>
                    <td colSpan="6">No drivers found</td>
                  </tr>
                ) : (
                  drivers.map((d) => (
                    <tr key={d.id}>
                      <td>{d.full_name || "-"}</td>
                      <td>{d.phone || "-"}</td>
                      <td>{d.prdp || "-"}</td>
                      <td>{d.prdp_expiry || "-"}</td>
                      <td>{d.assigned_truck || "-"}</td>
                      <td className="actions-cell">
                        <button
                          className="menu-button"
                          onClick={() => setActionModalDriver(d)}
                        >
                          ⋮
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {actionModalDriver && (
            <ActionModal
              driver={actionModalDriver}
              onEdit={openEditModal}
              onReassign={openReassignModal}
              onDelete={openDeleteConfirm}
              onClose={() => setActionModalDriver(null)}
              hasActiveTrips={hasActiveTrips}
            />
          )}
          {editingDriverId && (
            <div className="modal-overlay" style={{ zIndex: 1000 }}>
              <div className="modal" ref={modalRef} style={{ background: "white", padding: "20px", borderRadius: "5px", maxWidth: "500px", width: "100%" }}>
                <h3>Edit Driver: {form.full_name || "Unknown Driver"}</h3>
                <form onSubmit={updateDriver} className="form-block">
                  <input
                    placeholder="Full name"
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    required
                  />
                  <input
                    placeholder="Phone (10 digits)"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  />
                  <input
                    placeholder="ID/Passport"
                    value={form.id_number}
                    onChange={(e) => setForm({ ...form, id_number: e.target.value })}
                  />
                  <input
                    placeholder="PrDP No"
                    value={form.prdp}
                    onChange={(e) => setForm({ ...form, prdp: e.target.value })}
                  />
                  <input
                    type="date"
                    placeholder="PrDP Expiry"
                    value={form.prdp_expiry}
                    onChange={(e) => setForm({ ...form, prdp_expiry: e.target.value })}
                  />
                  {renderDropdown("Assigned Truck", "assigned_truck", trucks.map((t) => t.reg_no) || [])}
                  <div className="modal-actions" style={{ display: "flex", gap: "10px" }}>
                    <button type="submit" className="btn-primary">
                      Save
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                </form>
                {feedback && (
                  <FeedbackModal
                    message={feedback.message}
                    type={feedback.type}
                    onClose={() => setFeedback(null)}
                  />
                )}
              </div>
            </div>
          )}
          {reassignDriverId && (
            <div className="modal-overlay" style={{ zIndex: 1000 }}>
              <div className="modal" ref={modalRef} style={{ background: "white", padding: "20px", borderRadius: "5px", maxWidth: "500px", width: "100%" }}>
                <h3>Re-assign Truck for {drivers.find((d) => d.id === reassignDriverId)?.full_name || "Unknown Driver"}</h3>
                <form onSubmit={reassignTruck} className="form-block">
                  {renderDropdown("Assigned Truck", "assigned_truck", trucks.map((t) => t.reg_no) || [])}
                  <div className="modal-actions" style={{ display: "flex", gap: "10px" }}>
                    <button type="submit" className="btn-primary">
                      Save
                    </button>
                    <button type="button" className="btn-secondary" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                </form>
                {feedback && (
                  <FeedbackModal
                    message={feedback.message}
                    type={feedback.type}
                    onClose={() => setFeedback(null)}
                  />
                )}
              </div>
            </div>
          )}
          {confirmModal && (
            <ConfirmModal
              title={confirmModal.title}
              message={confirmModal.message}
              confirmText={confirmModal.confirmText}
              confirmVariant={confirmModal.confirmVariant}
              onConfirm={confirmModal.onConfirm}
              onClose={() => setConfirmModal(null)}
            />
          )}
        </>
      )}
    </div>
  );
}